import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useTranslation } from '@/hooks/useTranslation';
import { ArrowLeft, Save, Check, DollarSign, Tags } from 'lucide-react';
import { CategoryManager } from '@/components/CategoryManager';
import type { UserSettings } from '@/types';

const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'MGA', name: 'Malagasy Ariary', symbol: 'Ar' },
] as const;

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
] as const;

const THEMES = [
  { value: 'light', icon: '☀️' },
  { value: 'dark', icon: '🌙' },
  { value: 'system', icon: '💻' },
] as const;

export function Settings() {
  const { user, logout } = useAuth();
  const { settings, updateSettings } = useSettings();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [currency, setCurrency] = useState<UserSettings['currency']>(settings.currency);
  const [language, setLanguage] = useState<UserSettings['language']>(settings.language);
  const [theme, setTheme] = useState<UserSettings['theme']>(settings.theme);
  const [yearlyGoal, setYearlyGoal] = useState(settings.yearlyGoal.toString());
  const [defaultMonthlySalary, setDefaultMonthlySalary] = useState(
    (settings.defaultMonthlySalary || 0).toString()
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    setError(null);

    try {
      const newSettings = {
        currency,
        language,
        theme,
        yearlyGoal: parseFloat(yearlyGoal) || 0,
        defaultMonthlySalary: parseFloat(defaultMonthlySalary) || 0,
      };

      await updateSettings(newSettings);
      
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('❌ Error saving settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to save settings');
      alert(`Failed to save settings: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
      {/* Fixed Header */}
      <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/95 backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/95 flex-shrink-0">
        <div className="mx-auto w-full max-w-4xl px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex h-14 sm:h-16 items-center justify-between gap-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-1 sm:gap-2 rounded-lg sm:rounded-xl bg-slate-100 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm font-semibold text-slate-700 transition-all hover:bg-slate-200 active:scale-95 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600 min-h-[36px] sm:min-h-[44px]"
            >
              <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('nav.backToDashboard')}</span>
              <span className="sm:hidden">Back</span>
            </button>
            
            <h1 className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-base sm:text-xl md:text-2xl font-bold text-transparent truncate">
              {t('settings.title')}
            </h1>
            
            <div className="w-16 sm:w-24 md:w-32"></div>
          </div>
        </div>
      </header>

      {/* Scrollable Main Content */}
      <main className="flex-1 w-full overflow-y-auto overflow-x-hidden">
        <div className="min-h-full w-full">
          <div className="mx-auto w-full max-w-4xl px-3 py-4 sm:px-4 sm:py-6 md:px-6 md:py-8 lg:px-8">
            <div className="space-y-4 sm:space-y-6 pb-8">
              
              {/* Error Message */}
              {error && (
                <div className="rounded-lg sm:rounded-xl border-2 border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                  <p className="text-sm font-semibold text-red-900 dark:text-red-100">
                    ❌ Error: {error}
                  </p>
                  <p className="text-xs text-red-800 dark:text-red-200 mt-1">
                    Check browser console (F12) for details
                  </p>
                </div>
              )}

              {/* Profile Section */}
              <div className="rounded-lg sm:rounded-xl md:rounded-2xl border border-slate-200 bg-white/80 p-4 sm:p-5 md:p-6 shadow-lg sm:shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
                {/* FIX: removed hardcoded 👤 emoji — it's already inside the translation value */}
                <h2 className="mb-3 sm:mb-4 text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  {t('settings.profile')}
                </h2>
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Email
                    </label>
                    <p className="text-sm sm:text-base text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 rounded-lg px-3 py-2.5 sm:px-4 sm:py-3 truncate min-h-[44px] flex items-center">
                      {user?.email}
                    </p>
                  </div>
                  <button
                    onClick={logout}
                    className="w-full rounded-lg sm:rounded-xl bg-gradient-to-r from-red-500 to-pink-600 px-4 py-2.5 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold text-white shadow-lg transition-all hover:from-red-600 hover:to-pink-700 hover:shadow-xl active:scale-95 min-h-[44px]"
                  >
                    {t('auth.logout')}
                  </button>
                </div>
              </div>

              {/* Default Monthly Salary */}
              <div className="rounded-lg sm:rounded-xl md:rounded-2xl border border-slate-200 bg-white/80 p-4 sm:p-5 md:p-6 shadow-lg sm:shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
                <h2 className="mb-3 sm:mb-4 text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <DollarSign className="h-5 w-5 sm:h-6 sm:w-6" />
                  {t('settings.defaultSalary')}
                </h2>
                <input
                  type="number"
                  step="100"
                  value={defaultMonthlySalary}
                  onChange={(e) => setDefaultMonthlySalary(e.target.value)}
                  className="w-full rounded-lg sm:rounded-xl bg-slate-50 px-3 py-2.5 sm:px-4 sm:py-3 text-sm sm:text-base border-2 border-transparent focus:border-blue-500 dark:focus:border-purple-500 focus:outline-none transition-all text-slate-900 dark:bg-slate-700 dark:text-slate-100 min-h-[44px]"
                  placeholder="5000"
                />
                <div className="mt-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3 sm:p-4">
                  <p className="text-xs sm:text-sm text-blue-900 dark:text-blue-100">
                    {t('settings.defaultSalarySubtitle')}
                  </p>
                  <button
                    onClick={() => navigate('/recurring')}
                    className="mt-2 text-blue-600 dark:text-blue-400 font-semibold hover:underline"
                  >
                    {t('nav.recurring')} →
                  </button>
                </div>
              </div>

              {/* Currency */}
              <div className="rounded-lg sm:rounded-xl md:rounded-2xl border border-slate-200 bg-white/80 p-4 sm:p-5 md:p-6 shadow-lg sm:shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
                {/* FIX: removed hardcoded 💰 emoji — it's already inside the translation value */}
                <h2 className="mb-3 sm:mb-4 text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  {t('settings.currency')}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {CURRENCIES.map((curr) => (
                    <button
                      key={curr.code}
                      onClick={() => setCurrency(curr.code)}
                      className={`rounded-lg sm:rounded-xl border-2 p-3 sm:p-4 transition-all active:scale-95 min-h-[90px] sm:min-h-[100px] flex flex-col items-center justify-center ${
                        currency === curr.code
                          ? 'border-blue-500 dark:border-purple-500 bg-blue-50 dark:bg-purple-900/20'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <div className="text-xl sm:text-2xl mb-1">{curr.symbol}</div>
                      <div className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {curr.code}
                      </div>
                      <div className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 text-center">
                        {curr.name}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Language */}
              <div className="rounded-lg sm:rounded-xl md:rounded-2xl border border-slate-200 bg-white/80 p-4 sm:p-5 md:p-6 shadow-lg sm:shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
                {/* FIX: removed hardcoded 🌍 emoji — it's already inside the translation value */}
                <h2 className="mb-3 sm:mb-4 text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  {t('settings.language')}
                </h2>
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={`rounded-lg sm:rounded-xl border-2 p-3 sm:p-4 transition-all active:scale-95 min-h-[90px] sm:min-h-[100px] flex flex-col items-center justify-center ${
                        language === lang.code
                          ? 'border-blue-500 dark:border-purple-500 bg-blue-50 dark:bg-purple-900/20'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <div className="text-3xl sm:text-4xl mb-2">{lang.flag}</div>
                      <div className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {lang.name}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Theme */}
              <div className="rounded-lg sm:rounded-xl md:rounded-2xl border border-slate-200 bg-white/80 p-4 sm:p-5 md:p-6 shadow-lg sm:shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
                {/* FIX: removed hardcoded 🎨 emoji — it's already inside the translation value */}
                <h2 className="mb-3 sm:mb-4 text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  {t('settings.theme')}
                </h2>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {THEMES.map((themeOption) => (
                    <button
                      key={themeOption.value}
                      onClick={() => setTheme(themeOption.value as UserSettings['theme'])}
                      className={`rounded-lg sm:rounded-xl border-2 p-3 sm:p-4 transition-all active:scale-95 min-h-[90px] sm:min-h-[100px] flex flex-col items-center justify-center ${
                        theme === themeOption.value
                          ? 'border-blue-500 dark:border-purple-500 bg-blue-50 dark:bg-purple-900/20'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <div className="text-3xl sm:text-4xl mb-2">{themeOption.icon}</div>
                      <div className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-slate-100 capitalize">
                        {/* FIX: was t(`settings.theme_light`) → now t(`settings.light`) */}
                        {t(`settings.${themeOption.value as 'light' | 'dark' | 'system'}`)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Yearly Goal */}
              <div className="rounded-lg sm:rounded-xl md:rounded-2xl border border-slate-200 bg-white/80 p-4 sm:p-5 md:p-6 shadow-lg sm:shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
                {/* FIX: removed hardcoded 🎯 emoji — it's already inside the translation value */}
                <h2 className="mb-3 sm:mb-4 text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  {t('settings.yearlyGoal')}
                </h2>
                <input
                  type="number"
                  step="100"
                  value={yearlyGoal}
                  onChange={(e) => setYearlyGoal(e.target.value)}
                  className="w-full rounded-lg sm:rounded-xl bg-slate-50 px-3 py-2.5 sm:px-4 sm:py-3 text-sm sm:text-base border-2 border-transparent focus:border-blue-500 dark:focus:border-purple-500 focus:outline-none transition-all text-slate-900 dark:bg-slate-700 dark:text-slate-100 min-h-[44px]"
                  placeholder="10000"
                />
                <p className="mt-2 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  {t('settings.yearlyGoalSubtitle')}
                </p>
              </div>

              {/* Custom Categories */}
              <div className="rounded-lg sm:rounded-xl md:rounded-2xl border border-slate-200 bg-white/80 p-4 sm:p-5 md:p-6 shadow-lg sm:shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
                <h2 className="mb-1 text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Tags className="h-5 w-5 sm:h-6 sm:w-6" />
                  Custom Categories
                </h2>
                <p className="mb-4 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  Create your own income and expense categories to better organize your transactions.
                </p>
                <CategoryManager />
              </div>

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 sm:px-6 sm:py-4 text-sm sm:text-base font-semibold text-white shadow-lg transition-all hover:from-blue-700 hover:to-purple-700 hover:shadow-xl active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px] sm:min-h-[52px]"
              >
                {saved ? (
                  <>
                    <Check className="h-4 w-4 sm:h-5 sm:w-5" />
                    {t('settings.saved')}
                  </>
                ) : saving ? (
                  <>
                    <div className="h-4 w-4 sm:h-5 sm:w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    {t('settings.saving')}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 sm:h-5 sm:w-5" />
                    {t('settings.saveSettings')}
                  </>
                )}
              </button>

              {/* Branding */}
              <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-slate-200 dark:border-slate-700">
                <div className="text-center">
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mb-2">
                    MyBalance v1.0
                  </p>
                  <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">
                    MyBalance by{' '}
                    <span className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      Unova
                    </span>
                  </p>
                  <p className="text-[10px] sm:text-xs text-slate-400 dark:text-slate-500 mt-1">
                    © 2026 Unova. All rights reserved.
                  </p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </main>
    </div>
  );
}